import { useState } from "react";
import { useSearchParams } from "react-router";
import { Heading } from "@made-people/blueprint-heading";
import { InputField, Form } from "@made-people/blueprint-form";
import { ButtonAsButton, ButtonAsLink } from "@made-people/blueprint-button";
import { useTranslate } from "@made-people/grease-translations";
import { useAccountResetPassword } from "@made-people/grease-account";
import { useHref, useLink } from "@made-people/grease-link";
import { useResetPasswordForm } from "./hooks/use-reset-password-form";
import { ResetPasswordForm } from "./forms/ResetPasswordForm";
import { ErrorMessage } from "./forms/ErrorMessage";

type ActiveSection =
  | "reset"
  | "email-sent"
  | "set-new-password"
  | "password-changed";

export const AccountResetPasswordPageRoute = () => {
  const [searchParams] = useSearchParams();

  const id = searchParams.get("id");
  const i = searchParams.get("i");

  const initialSection: ActiveSection = id && i ? "set-new-password" : "reset";
  const [activeSection, setActiveSection] =
    useState<ActiveSection>(initialSection);

  return (
    <div className="mx-auto w-full max-w-screen-page-max-width md:px-component-spacing-lg xl:px-component-spacing-2xl">
      <div className="md:grid md:grid-cols-12 md:gap-x-1 min-h-[80vh]">
        <div className="md:col-start-4 md:col-span-6 w-full py-12 px-4 md:px-0">
          {activeSection === "reset" && (
            <ResetPasswordView
              onEmailSent={() => setActiveSection("email-sent")}
            />
          )}
          {activeSection === "email-sent" && <EmailSentView />}
          {activeSection === "set-new-password" && (
            <SetNewPasswordView
              onPasswordUpdated={() => setActiveSection("password-changed")}
            />
          )}
          {activeSection === "password-changed" && <PasswordChangedView />}
        </div>
      </div>
    </div>
  );
};

const ResetPasswordView = ({ onEmailSent }: { onEmailSent: () => void }) => {
  const translate = useTranslate();
  const href = useHref();
  const form = useResetPasswordForm({ onEmailSent });

  return (
    <div className="w-full">
      <ResetPasswordForm
        form={form}
        loginAction={
          <ButtonAsLink
            label={translate("Account_ResetPassword_SecondaryAction")}
            link={{ href: href("comfort-club/login") }}
            variants={{ style: "outline", width: "full" }}
          />
        }
      />
    </div>
  );
};

const EmailSentView = () => {
  const translate = useTranslate();
  const href = useHref();

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex flex-col gap-2 md:gap-4">
        <h2 className="text-heading-sm">
          {translate("Account_ResetPassword_EmailHasBeenSent_Heading")}
        </h2>
        <p className="text-body-md">
          {translate("Account_ResetPassword_EmailHasBeenSent_Lead")}
        </p>
      </div>
      <ButtonAsLink
        label={translate(
          "Account_ResetPassword_EmailHasBeenSent_PrimaryAction",
        )}
        link={{ href: href("comfort-club/login") }}
        variants={{ style: "solid", width: "full" }}
      />
    </div>
  );
};

const SetNewPasswordView = ({
  onPasswordUpdated,
}: {
  onPasswordUpdated: () => void;
}) => {
  const translate = useTranslate();
  const { execute, loadingState } = useAccountResetPassword();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    setError("");
    if (newPassword !== confirmPassword) {
      setError(translate("Account_ResetPassword_PasswordsDoNotMatch"));
      return;
    }

    const result = await execute({ newPassword });
    if (result.success) {
      onPasswordUpdated();
    } else {
      setError(translate(result.error));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSubmit();
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="flex flex-col gap-2 md:gap-4">
        <h2 className="text-heading-sm">
          {translate("Account_ResetPassword_SetNewPassword_Heading")}
        </h2>
        <p className="text-body-md">
          {translate("Account_ResetPassword_SetNewPassword_Lead")}
        </p>
      </div>
      <Form>
        <div className="flex flex-col gap-6">
          <InputField
            template="floating-label"
            type="password"
            label={translate("Form_Field_NewPassword_Label")}
            placeholder={translate("Form_Field_NewPassword_Placeholder")}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <InputField
            template="floating-label"
            type="password"
            label={translate("Form_Field_ConfirmNewPassword_Label")}
            placeholder={translate("Form_Field_ConfirmNewPassword_Placeholder")}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <ErrorMessage error={error} />
          <ButtonAsButton
            label={translate(
              "Account_ResetPassword_SetNewPassword_PrimaryAction",
            )}
            onClick={handleSubmit}
            progress={loadingState}
            variants={{ width: "full" }}
          />
        </div>
      </Form>
    </div>
  );
};

const PasswordChangedView = () => {
  const translate = useTranslate();
  const Link = useLink();

  return (
    <div className="w-full flex flex-col gap-6">
      <Heading
        heading={translate(
          "Account_ResetPassword_PasswordHasBeenChanged_Heading",
        )}
        lead={translate("Account_ResetPassword_PasswordHasBeenChanged_Lead")}
        headingTag="h1"
        variants={{ headingSize: "lg", leadSize: "sm" }}
      />
      <Link href="comfort-club/login" className="text-sm underline self-center">
        {translate(
          "Account_ResetPassword_PasswordHasBeenChanged_PrimaryAction",
        )}
      </Link>
    </div>
  );
};
